import React, {useEffect, useState} from 'react';
import {Text, View} from 'react-native';

import Checkbox from '/component/base/Checkbox/Checkbox';
import RadioButton from '/component/base/RadioButton/RadioButton';
import {styles as radioStyles} from '/component/base/RadioButton/RadioButton.style';
import BaseText from '/component/base/Text';
import TextInput from '/component/base/TextInput/TextInput';
import {Shared} from '/constant/TestIDs';
import testProperties from '/util/testing/testProperties';

import {
  EMPTY_ERRORS,
  NUMERIC_ONLY_ERROR,
} from '../../constants/VolPerfFilterRangeInputRadioGroup.constants';
import {sanitize} from '../../constants/volumePerformanceReport.constants';
import {RANGE_FILTER} from '../../constants/volumePerformanceReport.constants';
import {
  checkboxCheckedStyle,
  checkboxUncheckedStyle,
  styles,
} from '../../styles/VolPerfFilterRangeInputRadioGroup.style';
import {
  RangeId,
  ValidationErrors,
  VolPerfFilterRangeInputRadioGroupProps,
} from '../../types/VolPerfRange.types';
import {
  buildCheckboxLabelText,
  buildDisabledState,
  buildEnabledState,
  buildResetState,
  filterInput,
  getErrorField,
  getRangeOptions,
  isValidNumber,
  normalizeDigit,
  validateBetweenValues,
  validateCurrentInputs,
} from '../../util/VolPerfFilterRangeInputRadioGroup.util';

export const FilterRangeInputRadioGroup: React.FC<
  VolPerfFilterRangeInputRadioGroupProps
> = ({title = '', filterRangeDetails, setFilterRangeDetails, resetTrigger}) => {
  const {
    minDigit,
    maxDigit,
    resultRangeState,
    filterByText,
    aboveValue,
    belowValue,
    options,
    isFilterEnabled = false,
    keyboardType,
    isFloatAllowed,
    isNegativeNumAllowed,
    ValidationError,
  } = filterRangeDetails;

  const checkboxLabelText = buildCheckboxLabelText(filterByText);
  const [errors, setErrors] = useState<ValidationErrors>(EMPTY_ERRORS);
  const selectedRangeId = resultRangeState;
  const numberValidationConfig = {
    minDigit,
    maxDigit,
    isFloatAllowed,
    isNegativeNumAllowed,
  };

  useEffect(() => {
    if (resetTrigger !== undefined) {
      setErrors(EMPTY_ERRORS);
      setFilterRangeDetails(buildResetState);
    }
  }, [resetTrigger, setFilterRangeDetails]);

  // Update internal errors if the parent updates ValidationError (from synchronous check)
  useEffect(() => {
    if (ValidationError) {
      const nextErrors = validateCurrentInputs({
        isFilterEnabled,
        selectedRangeId,
        aboveValue,
        belowValue,
        ...numberValidationConfig,
      });
      setErrors(nextErrors);
    } else {
      setErrors(EMPTY_ERRORS);
    }
  }, [
    ValidationError,
    isFilterEnabled,
    selectedRangeId,
    aboveValue,
    belowValue,
  ]);

  const onChange = (index: number) => {
    const updatedOptions = options?.map((option, optionIndex) => ({
      ...option,
      selected: optionIndex === index,
    }));

    setFilterRangeDetails(previousState => ({
      ...previousState,
      options: updatedOptions,
    }));
  };

  const onChangeRange = (rangeId: RangeId) => {
    setErrors(EMPTY_ERRORS);
    setFilterRangeDetails(previousState => ({
      ...previousState,
      resultRangeState: rangeId,
      aboveValue: '',
      belowValue: '',
      isError: true,
      ValidationError: false,
    }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setErrors(EMPTY_ERRORS);
    setFilterRangeDetails(checked ? buildEnabledState : buildDisabledState);
  };

  const onChangeInputText = (
    selectedRange: RangeId,
    keyName: 'aboveValue' | 'belowValue',
    rawValue: string,
  ) => {
    let nextValue = rawValue;
    const sanitizedValue = filterInput(nextValue);

    if (sanitizedValue !== nextValue) {
      const nextErrors = {...errors};
      nextErrors[getErrorField(selectedRange, keyName)] = NUMERIC_ONLY_ERROR;
      setErrors(nextErrors);
      setFilterRangeDetails(previousState => ({
        ...previousState,
        resultRangeState: selectedRange,
        aboveValue:
          keyName === RANGE_FILTER.KEYS.ABOVE_VALUE &&
          selectedRange !== RANGE_FILTER.IDS.BELOW
            ? sanitizedValue
            : aboveValue,
        belowValue:
          keyName === RANGE_FILTER.KEYS.BELOW_VALUE &&
          selectedRange !== RANGE_FILTER.IDS.ABOVE
            ? sanitizedValue
            : belowValue,
        isError: true,
      }));
      return;
    }

    const nextErrors = {...errors};

    if (nextValue !== nextValue.trim()) {
      nextValue = nextValue.trim();
      if (
        nextValue === '' ||
        !isValidNumber(nextValue, numberValidationConfig)
      ) {
        return;
      }
    }

    if (!isNegativeNumAllowed && nextValue.includes('-')) {
      nextValue = nextValue.replace(/-/g, '');
      nextErrors[getErrorField(selectedRange, keyName)] = NUMERIC_ONLY_ERROR;
    }

    let isError = true;

    if (
      selectedRange === RANGE_FILTER.IDS.ABOVE ||
      selectedRange === RANGE_FILTER.IDS.BELOW
    ) {
      nextErrors.rangeValue = '';
      if (nextValue !== '' && nextValue !== '-') {
        isError = false;
      }
    } else {
      validateBetweenValues(
        keyName === RANGE_FILTER.KEYS.ABOVE_VALUE ? nextValue : aboveValue,
        keyName === RANGE_FILTER.KEYS.BELOW_VALUE ? nextValue : belowValue,
        nextErrors,
        numberValidationConfig,
      );
      const isAboveValid =
        (keyName === RANGE_FILTER.KEYS.ABOVE_VALUE ? nextValue : aboveValue) !==
          '' &&
        (keyName === RANGE_FILTER.KEYS.ABOVE_VALUE ? nextValue : aboveValue) !==
          '-';
      const isBelowValid =
        (keyName === RANGE_FILTER.KEYS.BELOW_VALUE ? nextValue : belowValue) !==
          '' &&
        (keyName === RANGE_FILTER.KEYS.BELOW_VALUE ? nextValue : belowValue) !==
          '-';
      isError =
        !!nextErrors.minValue ||
        !!nextErrors.maxValue ||
        !isAboveValid ||
        !isBelowValid;
    }

    setErrors(nextErrors);
    setFilterRangeDetails(previousState => ({
      ...previousState,
      resultRangeState: selectedRange,
      aboveValue:
        keyName === RANGE_FILTER.KEYS.ABOVE_VALUE &&
        selectedRange !== RANGE_FILTER.IDS.BELOW
          ? nextValue
          : aboveValue,
      belowValue:
        keyName === RANGE_FILTER.KEYS.BELOW_VALUE &&
        selectedRange !== RANGE_FILTER.IDS.ABOVE
          ? nextValue
          : belowValue,
      isError,
    }));
  };

  const normalizedTitle = sanitize(title);

  return (
    <View
      testID={`vol-perf-filter-${normalizedTitle}`}
      accessible={false}
      accessibilityRole="radiogroup"
      accessibilityLabel={title}>
      <BaseText
        style={[radioStyles.filterTypeText, styles.headerText]}
        {...testProperties(Shared.radioButtonGroupTitle)}
        accessible={true}
        accessibilityRole="header"
        isBold>
        {title}
      </BaseText>

      <View
        accessible={true}
        accessibilityLabel={checkboxLabelText}
        style={radioStyles.checkboxItem}>
        <Checkbox
          testID={`checkbox-${normalizedTitle}`}
          accessible={true}
          accessibilityLabel={`${checkboxLabelText}: ${
            isFilterEnabled ? 'SELECTED' : ''
          }`}
          checkedIconName={
            isFilterEnabled
              ? RANGE_FILTER.ICONS.CHECKED
              : RANGE_FILTER.ICONS.UNCHECKED
          }
          style={
            isFilterEnabled ? checkboxCheckedStyle : checkboxUncheckedStyle
          }
          rectangleCheckbox
          checked={isFilterEnabled}
          onChange={handleCheckboxChange}
        />
        <BaseText style={radioStyles.checkboxLabel} isBold>
          {checkboxLabelText}
        </BaseText>
      </View>

      {isFilterEnabled &&
        options &&
        options.length > 0 &&
        options.map((option, index) => (
          <RadioButton
            testID=""
            key={index}
            {...option}
            onPress={() => onChange(index)}
            isBold={true}
          />
        ))}
      {isFilterEnabled && options && options.length > 0 && (
        <View style={radioStyles.line} />
      )}

      {isFilterEnabled &&
        getRangeOptions(selectedRangeId).map(option => (
          <RadioButton
            testID={`radio-${normalizedTitle}-${option.id}`}
            key={option.id}
            text={option.text}
            selected={option.selected}
            onPress={() => onChangeRange(option.id)}
            isBold={true}
          />
        ))}

      {selectedRangeId === RANGE_FILTER.IDS.ABOVE && isFilterEnabled && (
        <View
          style={[
            styles.inputSectionSpacing,
            styles.inputSectionBottomSpacing,
          ]}>
          <TextInput
            testID={`input-${normalizedTitle}-above`}
            maxLength={normalizeDigit(maxDigit)}
            accessible={true}
            accessibilityLabel={`Text input for ${selectedRangeId} value`}
            placeholder={RANGE_FILTER.PLACEHOLDERS.RANGE_EG}
            value={aboveValue}
            onChangeText={(value: string) =>
              onChangeInputText(
                RANGE_FILTER.IDS.ABOVE as any,
                RANGE_FILTER.KEYS.ABOVE_VALUE as any,
                value,
              )
            }
            style={
              errors.rangeValue
                ? radioStyles.rangeInputError
                : radioStyles.rangeInput
            }
            keyboardType={keyboardType}
          />
          {errors.rangeValue ? (
            <Text style={[radioStyles.errorText, styles.errorTextWithMargin]}>
              {errors.rangeValue}
            </Text>
          ) : null}
        </View>
      )}

      {selectedRangeId === RANGE_FILTER.IDS.BELOW && isFilterEnabled && (
        <View
          style={[
            styles.inputSectionSpacing,
            styles.inputSectionBottomSpacing,
          ]}>
          <TextInput
            testID={`input-${normalizedTitle}-below`}
            maxLength={normalizeDigit(maxDigit)}
            accessible={true}
            accessibilityLabel={`Text input for ${selectedRangeId} value`}
            placeholder={RANGE_FILTER.PLACEHOLDERS.RANGE_EG}
            value={belowValue}
            onChangeText={(value: string) =>
              onChangeInputText(
                RANGE_FILTER.IDS.BELOW as any,
                RANGE_FILTER.KEYS.BELOW_VALUE as any,
                value,
              )
            }
            style={
              errors.rangeValue
                ? radioStyles.rangeInputError
                : radioStyles.rangeInput
            }
            keyboardType={keyboardType}
          />
          {errors.rangeValue ? (
            <Text style={[radioStyles.errorText, styles.errorTextWithMargin]}>
              {errors.rangeValue}
            </Text>
          ) : null}
        </View>
      )}

      {selectedRangeId === RANGE_FILTER.IDS.BETWEEN && isFilterEnabled && (
        <>
          <View
            style={[
              radioStyles.rangeInputContainer,
              styles.inputSectionSpacing,
            ]}>
            <TextInput
              testID={`input-${normalizedTitle}-min`}
              maxLength={normalizeDigit(maxDigit)}
              accessible={true}
              accessibilityLabel={`Text input for min value`}
              placeholder={RANGE_FILTER.PLACEHOLDERS.MIN}
              value={aboveValue}
              onChangeText={(value: string) =>
                onChangeInputText(
                  RANGE_FILTER.IDS.BETWEEN as any,
                  RANGE_FILTER.KEYS.ABOVE_VALUE as any,
                  value,
                )
              }
              style={
                errors.minValue
                  ? radioStyles.rangeInputBetweenError
                  : radioStyles.rangeInputBetween
              }
              keyboardType={keyboardType}
            />
            <View style={[radioStyles.line, styles.rangeSeparator]} />
            <TextInput
              testID={`input-${normalizedTitle}-max`}
              maxLength={normalizeDigit(maxDigit)}
              accessible={true}
              accessibilityLabel={`Text input for max value`}
              placeholder={RANGE_FILTER.PLACEHOLDERS.MAX}
              value={belowValue}
              onChangeText={(value: string) =>
                onChangeInputText(
                  RANGE_FILTER.IDS.BETWEEN as any,
                  RANGE_FILTER.KEYS.BELOW_VALUE as any,
                  value,
                )
              }
              style={
                errors.maxValue
                  ? radioStyles.rangeInputBetweenError
                  : radioStyles.rangeInputBetween
              }
              keyboardType={keyboardType}
            />
          </View>
          <View
            style={[
              radioStyles.rangeInputContainer,
              styles.inputSectionBottomSpacing,
            ]}>
            <View style={radioStyles.inputWithError}>
              {errors.minValue ? (
                <Text style={radioStyles.errorText}>{errors.minValue}</Text>
              ) : null}
            </View>
            <View style={radioStyles.inputWithError}>
              {errors.maxValue ? (
                <Text style={radioStyles.errorText}>{errors.maxValue}</Text>
              ) : null}
            </View>
          </View>
        </>
      )}
    </View>
  );
};

export default FilterRangeInputRadioGroup;
